import React, { useState } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";

import { useGetCurrentUserQuery } from "../../app/accounts/authuser";
import { useGetProjectQuery } from "../../app/services/projects";
import { useIsTwoColumn } from "../../hooks/useResponsiveLayout";
import { canEditCredit } from "../../utils/memberPositionUtils";
import DropdownMenu, { type DropdownMenuItem } from "../dropdown-menu/DropdownMenu";
import CreditEditModal from "../credit/CreditEditModal";
import CreditViewModal from "../credit/CreditViewModal";
import { useProjectShell } from "./ProjectShellContext";

/**
 * プロジェクト内の全タブ（ホーム / トークルーム / DM / プロジェクトの設定）で共通して
 * セグメントコントロール行の右端に置くプロジェクトメニュー（3 点リーダー）。
 * プロジェクトバナーと同様に、どのタブでも同じ導線を提供する。
 * クレジット・プロジェクトの設定などプロジェクト単位の操作を集約する。
 *
 * ノート（イメージボードを含む）/ ライブラリの表示中はパネルが画面を覆うため、
 * 通常タブの行は表示されない。代わりに ProjectWrapper がパネルの右上へ
 * FloatingProjectMenu でフロート配置する。
 *
 * DM / プロジェクトの設定のような 2 カラムレイアウトでは、右端のメニューを左カラム内の
 * 行に置くとカラム境界で止まってしまうため、`TwoColumnLayout` の
 * `topRightOverlay`（ビューポート右端）へ委譲する。
 */

/**
 * セグメントコントロールと同じ見た目の単独アイコンボタン（メニューのトリガー）。
 * 寸法・配色は SwitchComponent の SwitchWrapper（枠）+ IconOptionSpan
 * （アイコンセグメント: 幅 36px / アイコン 20px / hover #f0f0f0）を踏襲する。
 * アイコンの font-variation-settings はグローバルの
 * `.material-symbols-rounded` 設定（FILL 1 / wght 600）に任せる。
 */
const MenuTriggerButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 28px;
  padding: 0;
  flex-shrink: 0;
  /* セグメントコントロール / CommentField と同一のグラス */
  background-color: rgba(255, 255, 255, 0.16);
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(13px);
  -webkit-backdrop-filter: blur(13px);
  cursor: pointer;
  transition: 0.2s;

  .material-symbols-rounded {
    font-size: 20px;
  }

  @media (hover: hover) {
    &:hover {
      background-color: #f0f0f0;
    }
  }
`;

/**
 * ノート / ライブラリのパネル（ProjectWrapper の PanelTab）右上への
 * プロジェクトメニューのフロート配置用ラッパー。パネルには専用のアイコン行を
 * 設けず、コンテンツを上端まで広げた上で、アイコンだけをこの位置に重ねる。
 * top 10px は通常タブの 48px 行内でのアイコン縦位置（(48 - 28) / 2）、
 * right 16px はカード右端（.u-wrapper-full）と揃えるための値。
 */
export const FloatingProjectMenu = styled.div`
  position: absolute;
  top: 10px;
  right: 16px;
  z-index: 10;
`;

/**
 * プロジェクトメニュー（3 点リーダー）。行の右端・パネル右上・2 カラム
 * レイアウトの右上オーバーレイで共通して使う。
 *
 * クレジットはプロジェクトリスト（ProjectWheel）のアクションと同じく、
 * 編集権限があれば「クレジットを編集」、なければ「クレジットを観る」
 * （クレジット 0 件では無効）を出す。
 */
export const ProjectMenu: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { canOpenSettings, openSettings } = useProjectShell();
  const [creditModal, setCreditModal] = useState<"edit" | "view" | null>(null);

  const { data: currentUserData } = useGetCurrentUserQuery();
  const { data: projectData } = useGetProjectQuery(
    { project_id: projectId ?? "" },
    { skip: !projectId || !currentUserData },
  );
  const project = projectData?.data?.project;
  const canEditProjectCredit =
    !!project && canEditCredit(project.current_user_role ?? "");

  const items: DropdownMenuItem[] = [];
  if (project) {
    items.push(
      canEditProjectCredit
        ? {
            label: "クレジットを編集",
            icon: "view_list",
            onClick: () => setCreditModal("edit"),
          }
        : {
            label: "クレジットを観る",
            icon: "movie",
            disabled: project.credit_count === 0,
            onClick: () => setCreditModal("view"),
          },
    );
  }
  if (canOpenSettings) {
    items.push({ label: "プロジェクトの設定", icon: "settings", onClick: openSettings });
  }

  if (!projectId || items.length === 0) return null;
  return (
    <>
      <DropdownMenu
        items={items}
        trigger={
          <MenuTriggerButton type="button" aria-label="プロジェクトメニュー">
            <span className="material-symbols-rounded">more_horiz</span>
          </MenuTriggerButton>
        }
      />
      {creditModal === "edit" && (
        <CreditEditModal
          projectId={projectId}
          projectTitle={project?.code_name}
          canEdit={canEditProjectCredit}
          onClose={() => setCreditModal(null)}
        />
      )}
      {creditModal === "view" && (
        <CreditViewModal
          projectId={projectId}
          projectTitle={project?.code_name}
          onClose={() => setCreditModal(null)}
        />
      )}
    </>
  );
};

/**
 * TopAppBarHome（セグメントコントロール行）を使わないタブ（プロジェクトの設定）用の
 * 右端配置の 48px 行。横 padding 16px は Home のカード右端
 * （`.u-wrapper-full` = 左右 16px）と揃えるためのもの。
 */
const ProjectMenuBarRow = styled.div.attrs({ className: "u-row" })`
  width: 100%;
  height: 48px;
  padding: 0 16px;
  flex-shrink: 0;
  justify-content: flex-end;
`;

/**
 * プロジェクトの設定タブ（ProjectConfig）左カラム最上部のプロジェクトメニュー行。
 *
 * 2 カラム表示ではこの行の幅が左カラム幅止まりのため、メニューは行に置かず
 * `TwoColumnLayout` の `topRightOverlay`（ビューポート右端）へ委譲する
 * （行は他タブのセグメント行と縦位置を揃えるために残す）。1 カラム表示では
 * 行自体が画面幅いっぱいになるので、そのまま行の右端へ置く。
 */
export const ProjectMenuBar: React.FC = () => {
  const isTwoColumn = useIsTwoColumn();
  return (
    <ProjectMenuBarRow>
      {!isTwoColumn && <ProjectMenu />}
    </ProjectMenuBarRow>
  );
};
